import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { AdminShell } from '../components/admin/AdminShell';
import {
  WelcomeCard,
  SectionCard,
  ActivityItem,
  ActivityList,
  EmptyState
} from '../components/admin/DashboardComponents';
import { adminApi, ConversionMetricsResponse, PurposeBreakdownResponse, ErasureStats } from '../services/adminApi';
import { ConsentForm } from '../types/admin';
import SendConsentModal from '../components/SendConsentModal';
import { theme, blueGrey } from '../design-system/theme';
import { useRegion } from '../contexts/RegionContext';
import { formatDateTimeShort } from '../utils/dateFormat';
import {
  LoadingSpinner,
} from '../styles/adminShared';

// Page-specific styled components for dashboard stats
const DashboardStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background: white;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  border-left: 4px solid transparent;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 140px;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const StatIconWrapper = styled.div<{ $color?: string; $bgColor?: string }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: ${props => props.$color || 'inherit'};
  background: ${props => props.$bgColor || 'transparent'};
  margin-bottom: 4px;
  
  svg {
    width: 28px;
    height: 28px;
    stroke-width: 2;
  }
`;

const DashboardStatLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.textSecondary};
`;

const DashboardStatValue = styled.span<{ $color?: string }>`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.$color || theme.textPrimary};
  line-height: 1.2;
`;

const DashboardStatSubtext = styled.span`
  font-size: 13px;
  color: ${theme.textSubtle};
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

// Consent Lifecycle Bar
const LifecycleSection = styled.div`
  margin-bottom: 24px;
`;

const LifecycleBarContainer = styled.div`
  display: flex;
  width: 100%;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  background: #f1f5f9;
`;

const LifecycleSegment = styled.div<{ $width: number; $color: string }>`
  width: ${props => props.$width}%;
  min-width: ${props => (props.$width > 0 ? '2px' : '0')};
  height: 100%;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: white;
  transition: width 0.5s ease;
  position: relative;

  &:hover {
    opacity: 0.9;
  }
`;

const LifecycleLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${props => props.$color};
`;

const LegendLabel = styled.span`
  font-size: 13px;
  color: ${theme.textSecondary};
`;

const LegendValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.textPrimary};
`;

// Pending Actions
const PendingActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PendingActionCard = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.primary};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const PendingActionIcon = styled.div<{ $color: string; $bgColor: string }>`
  width: 48px;
  height: 48px;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 22px;
  color: ${props => props.$color};
  background: ${props => props.$bgColor};
`;

const PendingActionText = styled.div`
  flex: 1;
`;

const PendingActionValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${theme.textPrimary};
  line-height: 1.2;
`;

const PendingActionLabel = styled.div`
  font-size: 13px;
  color: ${theme.textSecondary};
  margin-top: 2px;
`;

interface DashboardStats {
  templates: number;
  fiduciaries: number;
  consents: number;
  projects: number;
}

interface UserInfo {
  email: string;
  role: string;
  tenantId?: string;
  name?: string;
  tenantName?: string;
}

export const Dashboard: React.FC = () => {
  const { selectedRegion } = useRegion();
  const [stats, setStats] = useState<DashboardStats>({ templates: 0, fiduciaries: 0, consents: 0, projects: 0 });
  const [recentConsents, setRecentConsents] = useState<any[]>([]);
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  
  // Lifecycle & pending actions state
  const [conversionMetrics, setConversionMetrics] = useState<ConversionMetricsResponse | null>(null);
  const [statusBreakdown, setStatusBreakdown] = useState<PurposeBreakdownResponse | null>(null);
  const [erasureStats, setErasureStats] = useState<ErasureStats | null>(null);
  const [expiringSoonCount, setExpiringSoonCount] = useState(0);

  // Send modal state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<ConsentForm | null>(null);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Load data from multiple endpoints in parallel
      // Pass regionCode to filter consent records by region
      const [templatesRes, fiduciariesRes, consentsRes, projectsRes, formsRes, conversionRes, statusRes, erasureRes, consentsPageRes] = await Promise.allSettled([
        adminApi.listTemplates(undefined, 0, 1),
        adminApi.listDataCollectors(1, 1),
        adminApi.listConsentRecords(1, 5, undefined, undefined, selectedRegion.code),
        adminApi.listProjects(1, 1),
        adminApi.listForms(1, 10),
        adminApi.getConversionMetrics(selectedRegion.code),
        adminApi.getConsentByPurpose(selectedRegion.code),
        adminApi.getErasureStats(),
        adminApi.listConsentRecords(1, 100, undefined, undefined, selectedRegion.code),
      ]);

      const newStats: DashboardStats = {
        templates: templatesRes.status === 'fulfilled' ? (templatesRes.value?.pagination?.totalItems || templatesRes.value?.totalElements || templatesRes.value?.data?.length || templatesRes.value?.content?.length || 0) : 0,
        fiduciaries: fiduciariesRes.status === 'fulfilled' ? (fiduciariesRes.value?.pagination?.totalItems || fiduciariesRes.value?.totalElements || fiduciariesRes.value?.data?.length || fiduciariesRes.value?.content?.length || 0) : 0,
        consents: consentsRes.status === 'fulfilled' ? (consentsRes.value?.pagination?.totalItems || consentsRes.value?.totalElements || consentsRes.value?.data?.length || consentsRes.value?.content?.length || 0) : 0,
        projects: projectsRes.status === 'fulfilled' ? (projectsRes.value?.pagination?.totalItems || projectsRes.value?.totalElements || projectsRes.value?.data?.length || projectsRes.value?.content?.length || 0) : 0,
      };

      setStats(newStats);

      // Get recent consents for display
      if (consentsRes.status === 'fulfilled') {
        const consents = consentsRes.value?.data || consentsRes.value?.content || [];
        setRecentConsents(consents.slice(0, 5));
      }

      // Get forms for quick send - deduplicate by formId
      if (formsRes.status === 'fulfilled') {
        const formsList = formsRes.value?.data || formsRes.value?.content || [];
        const activeFormsMap = new Map<string, ConsentForm>();
        formsList
          .filter((f: ConsentForm) => f.active !== false)
          .forEach((f: ConsentForm) => {
            if (f.formId && !activeFormsMap.has(f.formId)) {
              activeFormsMap.set(f.formId, f);
            }
          });
        setForms(Array.from(activeFormsMap.values()));
      }

      // Set conversion metrics for lifecycle funnel
      if (conversionRes.status === 'fulfilled' && conversionRes.value) {
        setConversionMetrics(conversionRes.value);
      }

      // Set status breakdown for lifecycle funnel
      if (statusRes.status === 'fulfilled' && statusRes.value) {
        setStatusBreakdown(statusRes.value);
      }

      // Set erasure stats for pending actions
      if (erasureRes.status === 'fulfilled' && erasureRes.value) {
        setErasureStats(erasureRes.value);
      }

      // Compute expiring soon from consent records
      if (consentsPageRes.status === 'fulfilled') {
        const allConsents = consentsPageRes.value?.data || consentsPageRes.value?.content || [];
        const now = new Date();
        const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        const expiring = allConsents.filter((c: any) => {
          if (!c.expiresAt) return false;
          const expiryDate = new Date(c.expiresAt);
          return expiryDate > now && expiryDate <= thirtyDaysFromNow && c.status !== 'WITHDRAWN' && c.status !== 'EXPIRED';
        });
        setExpiringSoonCount(expiring.length);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedRegion]);

  useEffect(() => {
    // Get user info from localStorage
    const storedUser = localStorage.getItem('userInfo');
    if (storedUser) {
      try {
        setUserInfo(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse user info');
      }
    }

    loadDashboardData();
  }, [loadDashboardData]);

  const formatDate = (dateString: string) => {
    return formatDateTimeShort(dateString);
  };

  if (loading) {
    return (
      <AdminShell>
        <LoadingSpinner>Loading dashboard...</LoadingSpinner>
      </AdminShell>
    );
  }

  const quickActions = [
    { 
      label: 'Send Consent', 
      href: '/admin/consent-forms',
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
    },
    { 
      label: 'Templates', 
      href: '/admin/templates',
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
    },
    { 
      label: 'Fiduciaries', 
      href: '/admin/fiduciaries',
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
    },
    { 
      label: 'View Consents', 
      href: '/admin/consents',
      icon: <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    },
  ];

  return (
    <AdminShell>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-tour="dashboard">
        {/* Welcome Section */}
        <WelcomeCard
          userName={userInfo?.email?.split('@')[0]}
          quickActions={quickActions}
        />

        {/* Stats Grid */}
        <DashboardStatsGrid>
          <StatCardWrapper>
            <StatIconWrapper $color={theme.primary} $bgColor="rgba(76, 132, 201, 0.1)">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </StatIconWrapper>
            <DashboardStatLabel>Total Templates</DashboardStatLabel>
            <DashboardStatValue $color={theme.primary}>{stats.templates}</DashboardStatValue>
            <DashboardStatSubtext>Active consent templates</DashboardStatSubtext>
          </StatCardWrapper>
          <StatCardWrapper>
            <StatIconWrapper $color={theme.success} $bgColor="rgba(76, 175, 80, 0.1)">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            </StatIconWrapper>
            <DashboardStatLabel>Data Fiduciaries</DashboardStatLabel>
            <DashboardStatValue $color={theme.success}>{stats.fiduciaries}</DashboardStatValue>
            <DashboardStatSubtext>Registered organizations</DashboardStatSubtext>
          </StatCardWrapper>
          <StatCardWrapper>
            <StatIconWrapper $color={theme.accent} $bgColor="rgba(233, 30, 99, 0.1)">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </StatIconWrapper>
            <DashboardStatLabel>Consent Artifacts</DashboardStatLabel>
            <DashboardStatValue $color={theme.accent}>{stats.consents}</DashboardStatValue>
            <DashboardStatSubtext>Total consents collected</DashboardStatSubtext>
          </StatCardWrapper>
          <StatCardWrapper>
            <StatIconWrapper $color={blueGrey[7]} $bgColor="rgba(123, 136, 152, 0.1)">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
            </StatIconWrapper>
            <DashboardStatLabel>Active Projects</DashboardStatLabel>
            <DashboardStatValue $color={blueGrey[7]}>{stats.projects}</DashboardStatValue>
            <DashboardStatSubtext>Running projects</DashboardStatSubtext>
          </StatCardWrapper>
        </DashboardStatsGrid>

        {/* Consent Lifecycle Funnel */}
        {(() => {
          const total = conversionMetrics?.total || stats.consents || 0;
          const withdrawn = statusBreakdown?.breakdown?.find(b => b.status === 'WITHDRAWN')?.count || 0;
          const expired = statusBreakdown?.breakdown?.find(b => b.status === 'EXPIRED')?.count || 0;
          const pending = conversionMetrics?.pending || 0;
          const active = Math.max(0, total - pending - withdrawn - expired);

          const segments = [
            { label: 'Pending', value: pending, color: '#f59e0b' },
            { label: 'Active', value: active, color: '#22c55e' },
            { label: 'Withdrawn', value: withdrawn, color: '#ef4444' },
            { label: 'Expired', value: expired, color: '#94a3b8' },
          ];

          return (
            <LifecycleSection>
              <SectionCard title="Consent Lifecycle" icon={<span>🔄</span>}>
                {total === 0 ? (
                  <EmptyState
                    icon="📊"
                    title="No lifecycle data yet"
                    text="Consent lifecycle data will appear once consents are collected."
                  />
                ) : (
                  <>
                    <div style={{ fontSize: '14px', color: theme.textSecondary, marginBottom: '16px' }}>
                      Total Sent: <strong style={{ color: theme.textPrimary }}>{total}</strong>
                    </div>
                    <LifecycleBarContainer>
                      {segments.map((seg) => {
                        const pct = total > 0 ? (seg.value / total) * 100 : 0;
                        return pct > 0 ? (
                          <LifecycleSegment key={seg.label} $width={pct} $color={seg.color}>
                            {pct >= 8 ? seg.value : ''}
                          </LifecycleSegment>
                        ) : null;
                      })}
                    </LifecycleBarContainer>
                    <LifecycleLegend>
                      {segments.map((seg) => (
                        <LegendItem key={seg.label}>
                          <LegendDot $color={seg.color} />
                          <LegendLabel>{seg.label}</LegendLabel>
                          <LegendValue>{seg.value}</LegendValue>
                        </LegendItem>
                      ))}
                    </LifecycleLegend>
                  </>
                )}
              </SectionCard>
            </LifecycleSection>
          );
        })()}

        {/* Pending Actions */}
        <div style={{ marginBottom: '24px' }}>
          <SectionCard title="Pending Actions" icon={<span>⚡</span>}>
            <PendingActionsGrid>
              <PendingActionCard href="/admin/consents">
                <PendingActionIcon $color="#f59e0b" $bgColor="rgba(245, 158, 11, 0.1)">
                  ⏰
                </PendingActionIcon>
                <PendingActionText>
                  <PendingActionValue>{expiringSoonCount}</PendingActionValue>
                  <PendingActionLabel>Consents expiring within 30 days</PendingActionLabel>
                </PendingActionText>
              </PendingActionCard>
              <PendingActionCard href="/admin/data-erasure">
                <PendingActionIcon $color="#ef4444" $bgColor="rgba(239, 68, 68, 0.1)">
                  🗑️
                </PendingActionIcon>
                <PendingActionText>
                  <PendingActionValue>{erasureStats?.pendingConfirmation || 0}</PendingActionValue>
                  <PendingActionLabel>Erasure confirmations pending</PendingActionLabel>
                </PendingActionText>
              </PendingActionCard>
            </PendingActionsGrid>
          </SectionCard>
        </div>

        {/* Two Column Layout */}
        <TwoColumnLayout>
          {/* Recent Consents */}
          <SectionCard
            title="Recent Consent Artifacts"
            icon={<span>📝</span>}
            action={{ label: 'View All', href: '/admin/consents' }}
          >
            {recentConsents.length > 0 ? (
              <ActivityList>
                {recentConsents.map((consent, index) => (
                  <ActivityItem
                    key={consent.consentId || index}
                    title={consent.dataOwnerId 
                      ? (consent.dataOwnerId.length > 25 
                        ? `${consent.dataOwnerId.substring(0, 25)}...` 
                        : consent.dataOwnerId)
                      : 'Unknown Data Owner'}
                    time={formatDate(consent.submittedAt || consent.createdAt)}
                    badge={{
                      label: consent.status || (consent.consentAccepted ? 'ACCEPTED' : 'PENDING'),
                      variant: consent.consentAccepted ? 'success' : consent.status === 'WITHDRAWN' ? 'warning' : 'info'
                    }}
                  />
                ))}
              </ActivityList>
            ) : (
              <EmptyState
                icon="📭"
                title="No consent artifacts yet"
                text="Start by creating templates and generating consent URLs."
              />
            )}
          </SectionCard>

          {/* Tenant Info */}
          <SectionCard title="Tenant Information" icon={<span>ℹ️</span>}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {userInfo?.tenantName && (
                <div style={{ padding: '12px', background: blueGrey[10], borderRadius: '8px', color: 'white' }}>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>Organization</div>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>{userInfo.tenantName}</div>
                </div>
              )}
              <div style={{ padding: '12px', background: 'var(--ds-surface-muted, #f7f7fb)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: 'var(--ds-text-subtle)', marginBottom: '4px' }}>Tenant ID</div>
                <div style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                  {userInfo?.tenantId || 'N/A'}
                </div>
              </div>
              <div style={{ padding: '12px', background: 'var(--ds-surface-muted, #f7f7fb)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: 'var(--ds-text-subtle)', marginBottom: '4px' }}>Your Role</div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{userInfo?.role || 'ADMIN'}</div>
              </div>
              <div style={{ padding: '12px', background: 'var(--ds-surface-muted, #f7f7fb)', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: 'var(--ds-text-subtle)', marginBottom: '4px' }}>Email</div>
                <div style={{ fontSize: '14px' }}>{userInfo?.email || 'N/A'}</div>
              </div>
            </div>
          </SectionCard>
        </TwoColumnLayout>

        {/* Quick Send Section - Improved with form details */}
        {forms.length > 0 && (
          <SectionCard
            title="Quick Send Consent"
            icon={<span>📤</span>}
            action={{ label: 'View All Forms', href: '/admin/consent-forms' }}
          >
            <p style={{ color: 'var(--ds-text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
              Select a consent form to send to a data owner via email or SMS
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {forms.slice(0, 4).map((form) => (
                <div
                  key={form.formId}
                  style={{
                    padding: '16px',
                    background: 'white',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => {
                    setSelectedForm(form);
                    setSendModalOpen(true);
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: theme.textPrimary }}>
                      {form.formTitle || 'Untitled Form'}
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      background: form.active !== false ? '#dcfce7' : '#fee2e2', 
                      color: form.active !== false ? '#166534' : '#991b1b',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 500
                    }}>
                      {form.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '12px' }}>
                    {form.formDescription || `${form.templateIds?.length || 0} template(s) attached`}
                  </div>
                  <button
                    style={{
                      padding: '8px 16px',
                      background: theme.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      width: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    📤 Send Consent Request
                  </button>
                </div>
              ))}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Send Consent Modal */}
      {selectedForm && (
        <SendConsentModal
          isOpen={sendModalOpen}
          onClose={() => {
            setSendModalOpen(false);
            setSelectedForm(null);
          }}
          formId={selectedForm.formId || ''}
          formTitle={selectedForm.formTitle}
        />
      )}
    </AdminShell>
  );
};
